import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Product} from '../../../generic/classes/product';
import {ActiveOrderService} from '../../../generic/service/active.order.service';
import {ApplicationService} from '../../../generic/service/application.service';
import {ProductService} from '../../../generic/service/product.service';
import {ShoppingCartService} from '../../../generic/service/shoppingcart.service';
import {TagManagerService} from '../../../generic/service/tag.manager.service';
import {UserService} from '../../../generic/service/user.service';
import {DecimalPipe} from '@angular/common';
import {RenderCheckoutIconComponent} from '../../../generic/component/cart/render.checkout.icon';
import {ProductQuantityUtils} from '../../../generic/utils/product.quantity.utils';

@Component({
	selector: 'buy-unit',
	templateUrl: 'buy.unit.component.html',
	styleUrls: ['./buy.component.scss'],
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		RenderCheckoutIconComponent,
		DecimalPipe,
	],
})
export class BuyUnitComponent implements OnInit, OnChanges {
	private shoppingCartService = inject(ShoppingCartService);
	private formBuilder = inject(UntypedFormBuilder);
	private userService = inject(UserService);
	private activeOrderService = inject(ActiveOrderService);
	private productService = inject(ProductService);
	private tagManagerService = inject(TagManagerService);
	private changeDetectorRef = inject(ChangeDetectorRef);
	applicationService = inject(ApplicationService);

	@Input() public product: Product;
	@Input() itemPrice: number;
	@Input() itemDiscountPrice: number;
	@Input() itemsPerUnit: number;
	@Input() itemsInOrder: number;
	@Input() public unitDescription: string;
	@Input() disabled: boolean;

	minimumOrderQuantity: number;
	public originalPrice: number;
	public unitPrice: number;
	public quantityValue: number;
	public addToCart: UntypedFormGroup;

	public buttonShowPressed: boolean = false;
	public showConfirmation: boolean = false;

	ngOnChanges(changes: SimpleChanges): void {
		this.unitPrice = this.product.saleDiscount > 0 ? this.itemDiscountPrice : this.itemPrice;
	}

	isFastCheckout(): boolean {
		return this.userService.isUseFastCheckout();
	}

	ngOnInit() {
		this.addToCart = this.formBuilder.group({
			quantity: ['', Validators.required],
		});

		this.minimumOrderQuantity = this.getMinimumOrderQuantity();
		this.setQuantityValue(this.minimumOrderQuantity);
		this.addToCartQuantityChanges();

		this.originalPrice = this.itemPrice * this.itemsPerUnit;
		this.unitPrice = this.product.saleDiscount > 0 ? this.itemDiscountPrice : this.itemPrice;
	}

	isEnabled() {
		const maximumOrderQuantity = this.getMaximumOrderQuantity();
		return maximumOrderQuantity === null || this.minimumOrderQuantity <= maximumOrderQuantity;
	}

	/**
	 * @param value
	 */
	onSubmit(value: any) {
		this.changeDetectorRef.markForCheck();
		if (this.buttonShowPressed) {
			return;
		}

		const maximumOrderQuantity = this.getMaximumOrderQuantity();
		if (maximumOrderQuantity && this.quantityValue > maximumOrderQuantity) {
			return;
		}

		const numberOfItemsToOrder: number = this.quantityValue * this.itemsPerUnit;

		// Don't show the spinner if the user is first to select an order in the popup.
		if (this.activeOrderService.hasSelectedOrder()) {
			this.buttonShowPressed = true;
		}

		const productOrderDone = this.shoppingCartService.orderProduct(
			this.product,
			numberOfItemsToOrder,
			false
		);

		// Can be null if the user had first to select a (new) active order.
		if (productOrderDone == null) {
			this.buttonShowPressed = false;
			return;
		}

		productOrderDone.subscribe(
			() => {
				if (this.buttonShowPressed) {
					this.showConfirmation = true;
					this.setQuantityValue(this.minimumOrderQuantity);

					this.productService.triggerProductAddedToCartEvent(this.product);

					this.tagManagerService.trackAddToCart(
						this.product,
						numberOfItemsToOrder,
						numberOfItemsToOrder * this.unitPrice,
					);

					this.changeDetectorRef.markForCheck();

					setTimeout(() => {
						this.showConfirmation = false;
						this.buttonShowPressed = false;
						this.changeDetectorRef.markForCheck();
					}, 2000);
				}
			},
			() => {
				this.buttonShowPressed = false;
				this.changeDetectorRef.markForCheck();
			},
		);
	}

	/**
	 * Checks if the combination of quantity and order unit is not less than the minimum quantity or
	 * larger than the available quantity.
	 */
	public changeQuantity() {
		const minimum = this.minimumOrderQuantity;

		if (minimum > this.quantityValue) {
			// Can not order less than minimum.
			this.setQuantityValue(minimum);
			return;
		}

		const maximum = this.getMaximumOrderQuantity();

		if (maximum !== null && maximum > 0 && this.quantityValue > maximum) {
			// Can not order more than maximum.
			this.setQuantityValue(maximum);
			return;
		}
	}

	public minDisabled(): boolean {
		return this.quantityValue <= this.minimumOrderQuantity;
	}

	public plusDisabled(): boolean {
		const maximumOrderQuantity = this.getMaximumOrderQuantity();
		return maximumOrderQuantity !== null && this.quantityValue >= maximumOrderQuantity;
	}

	/**
	 * Check if quantity + 1 is available
	 */
	public plusQuantity() {
		const quantityPlusOne = this.quantityValue + 1;
		const maximumOrderQuantity = this.getMaximumOrderQuantity();

		if (maximumOrderQuantity == null || quantityPlusOne <= maximumOrderQuantity) {
			this.setQuantityValue(quantityPlusOne);
		}
	}

	/**
	 * Check if quantity -1 is possible
	 */
	public minQuantity() {
		const quantityMinusOne = this.quantityValue - 1;

		if (quantityMinusOne >= this.minimumOrderQuantity) {
			this.setQuantityValue(quantityMinusOne);
		}
	}

	getMaximumOrderQuantity(): number | null {
		return ProductQuantityUtils.getMaxOrderQuantity(this.itemsPerUnit, this.product, this.itemsInOrder);
	}

	/**
	 * Sets the quantity value of the form input element
	 */
	private setQuantityValue(newQuantity: number) {
		this.quantityValue = newQuantity;
		this.addToCart.get('quantity')?.setValue(newQuantity);
	}

	/**
	 * Listen to changes made to the quantity and update the correct variables
	 */
	private addToCartQuantityChanges(): void {
		this.addToCart.valueChanges.subscribe(value => this.quantityValue = value.quantity);
	}

	private getMinimumOrderQuantity(): number {
		return ProductQuantityUtils.getMinOrderQuantity(this.product, this.itemsPerUnit);
	}
}
